import { createContext, useCallback, useContext, useRef, useState } from 'react';
import useLocalStorage from 'react-use-localstorage';
import { exitFullscreen, goFullscreen } from '../helpers/fullscreen';
import { isTrueMobile } from '../helpers/isDevice';

const WebChatContext = createContext(null);

function WebChatProvider({ children, tokenUrl, tokenMethod, lang = 'fr-ca', faqOnly = false, privacyPolicyUrl }) {
  tokenUrl = tokenUrl || 'https://webchat-mockbot.azurewebsites.net/directline/token';
  tokenMethod = tokenMethod || 'POST';

  const [loaded, setLoaded] = useState(false);
  const [spinnerDisplayed, setSpinnerDisplayed] = useState(true);
  const [minimized, setMinimized] = useState(true);
  const [newMessage, setNewMessage] = useState(false);
  const [token, setToken] = useState();
  const [activeTab, setTab] = useState(true);
  const [propLang, setLang] = useState(lang);
  const tokenQueryInfo = useRef({ tokenUrl, tokenMethod })
  const reduxStore = useRef(null);
  const [reset, setReset] = useState(false);
  const [storageUserId, setStorageUserId] = useLocalStorage('webchat_userid', '');
  const [storageToken, setStorageToken] = useLocalStorage('webchat_token', '');

  const fetchToken = async () => {
    const { tokenUrl, tokenMethod } = tokenQueryInfo.current;
    const url = storageUserId ? tokenUrl + '?userid=' + encodeURIComponent(storageUserId) : tokenUrl;
    const res = await fetch(url, { method: tokenMethod });
    const { token, userId } = await res.json();
    setToken(token)

    // session storage
    setStorageToken(token);
    setStorageUserId(userId);
    return token;
  }

  const shadowRootId = useRef({ id: faqOnly ? 'shadow-host-faq' : 'shadow-host-bubble' });

  const [menuOpen, setMenuOpen] = useState(false);

  const openMenu = () => {
    setMenuOpen(true);
  }

  const closeMenu = () => {
    setMenuOpen(false);
  }


  const handleMaximizeButtonClick = useCallback(async () => {
    setLoaded(true);
    setMinimized(false);
    setNewMessage(false);

    window.dataLayer?.push({
      'event': 'click',
      'eventName': 'open_chatbot',
      'eventType': 'chatbot'
    })

    if (isTrueMobile()) {
      const element = document.querySelector(`#${shadowRootId.current.id}`)
      goFullscreen(element);
    }

  }, [setMinimized, setNewMessage]);

  const handleMinimizeButtonClick = useCallback(() => {
    setMinimized(true);
    setNewMessage(false);

    if (isTrueMobile()) {
      exitFullscreen();
    }

  }, [setMinimized, setNewMessage]);

  return (
    <WebChatContext.Provider value={{
      ...{
        loaded,
        setLoaded,
        setSpinnerDisplayed,
        spinnerDisplayed,
        minimized,
        setMinimized,
        newMessage,
        setNewMessage,
        token,
        setToken,
        handleMaximizeButtonClick,
        handleMinimizeButtonClick,
        privacyPolicyUrl,
        activeTab,
        setTab,
        tokenQueryInfo,
        shadowRootId,
        'lang': propLang,
        setLang,
        menuOpen,
        openMenu,
        closeMenu,
        reduxStore,
        reset,
        setReset,
        fetchToken
      }
    }}>
      {children}
    </WebChatContext.Provider>
  );
}

function useWebChat() {
  const context = useContext(WebChatContext);

  if (context == null) {
    return;
  }

  return context;
}

export { WebChatProvider, useWebChat };
